import { useEffect, useState, useSyncExternalStore } from 'react';
import '../../css/industrial.css';

// ── Tiny module-level toast store (mirrors the north-star mock's toast()). ──
// Module-level so any Industrial page can `toast(msg)` without context wiring.
let _toast = null;          // { id, msg, type }
let _seq = 0;
const _subs = new Set();
let _timer = null;
export function toast(msg, type = 'ok') {
  _toast = { id: ++_seq, msg, type };
  _subs.forEach(fn => fn());
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => { _toast = null; _subs.forEach(fn => fn()); }, 2600);
}
function _sub(cb) { _subs.add(cb); return () => _subs.delete(cb); }
function _snap() { return _toast; }

export function ToastHost() {
  const t = useSyncExternalStore(_sub, _snap, _snap);
  if (!t) return null;
  return (
    <div key={t.id} className={`ind-toast ${t.type}`} role="status">
      <span className="ind-toast-dot" />{t.msg}
    </div>
  );
}

// Shared header/frame for every Industrial console page. Renders inside the
// app's existing <Layout> (Navbar + Sidebar), so this is page-level chrome only.
export default function IndustrialLayout({ eyebrow, title, subtitle, right, banner, toolbar, children, split }) {
  return (
    <div className="ind-root" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <header className="ind-head">
        <div>
          <div className="ind-eye">{eyebrow || 'Industrial'}</div>
          <h1 className="ind-h1">{title}</h1>
          {subtitle && <div className="ind-sub">{subtitle}</div>}
        </div>
        <div className="ind-head-right">{right ?? <ArmedClock />}</div>
      </header>
      {/* Full-width control strip under the title. Filters and whole-set actions
          live here rather than in a 340px column, where they crowded out the very
          list they filter — and here they get the page's full width. */}
      {toolbar && <div className="ind-toolbar">{toolbar}</div>}
      {banner}
      {split
        ? <div className="ind-ops" style={{ flex: 1, minHeight: 0 }}>{children}</div>
        : <div className="ind-body" style={{ flex: 1, minHeight: 0 }}>{children}</div>}
      <ToastHost />
    </div>
  );
}

// Live clock + armed indicator, mirroring the north-star OSD.
export function ArmedClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return (
    <>
      <span className="ind-clk">{hh}:{mm}:{ss}</span>
      <span>|</span>
      <span className="ind-armed"><span className="dot" />ARMED</span>
    </>
  );
}
